import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { FavoriteItem, FavoriteItemType } from '../../modules/auth'
import { encodeSlides, LvcPrimarySliderItem } from '../../modules/sliderUtils'
import { Locale } from '../../modules/locale'
import { Thumbs } from 'swiper'
import { Font } from '../../constants'
import { mapEvent } from '../../modules/utils'
import MainModule from '../../modules/main'

Locale.getInstance().registerKeySet('fr', 'favoritesList', {
  artists: 'Artistes',
  places: 'Lieux',
  events: 'Événements',
  exhibitions: 'Expositions',
  eventSeries: 'Festivals et séries',
  permanentExhibitions: 'Expositions permanentes',
  eventsCount: {
    plural: 'événements',
    singular: 'événement',
  },
  exhibitionsCount: {
    plural: 'expositions',
    singular: 'exposition',
  },
  noFavoriteArtistsTitle: "Vous n'avez pas d'artiste favori",
  noFavoriteArtistsDescription: 'Pour ajouter un artiste à votre liste, cliquez sur Favoris',
  noFavoriteArtistsCardTitle: "Nom de l'artiste",
  noFavoriteArtistsCardInfo: '4 événements',
  noFavoriteEventsTitle: "Vous n'avez pas d'événements favoris",
  noFavoriteEventsDescription: 'Pour ajouter un événement à votre liste, cliquez sur Favoris',
  noFavoriteExhibitionsTitle: "Vous n'avez pas d'expositions favorites",
  noFavoriteExhibitionsDescription:
    'Pour ajouter une exposition à votre liste, cliquez sur Favoris',
  noFavoriteExhibitionsCardTitle: "Nom de l'exposition",
  noFavoriteExhibitionsCardInfo: '4 expositions',
  noFavoriteEventsCardTitle: "Nom de l'événement",
  noFavoriteEventsCardInfo: '4 événements',
  noFavoriteEventSeriesTitle: "Vous n'avez pas de festival ou de série favoris",
  noFavoriteEventSeriesDescription:
    'Pour ajouter un festival ou une série à votre liste de favoris, cliquez sur Favoris',
  noFavoriteEventSeriesCardTitle: 'Nom du festival ou de la série',
  noFavoriteEventSeriesCardInfo: '4 festivals ou séries',
  noFavoritePlacesTitle: "Vous n'avez pas de lieu favori",
  noFavoritePlacesDescription: 'Pour ajouter un lieu à votre liste, cliquez sur Favoris',
  noFavoritePlacesCardTitle: 'Nom du lieu',
  noFavoritePlacesCardInfo: '4 événements',
})

Locale.getInstance().registerKeySet('en', 'favoritesList', {
  artists: 'Artists',
  places: 'Places',
  events: 'Events',
  exhibitions: 'Exhibitions',
  eventSeries: 'Festivals and series',
  permanentExhibitions: 'Permanent exhibitions',
  eventsCount: {
    plural: 'events',
    singular: 'event',
  },
  exhibitionsCount: {
    plural: 'exhibitions',
    singular: 'exhibition',
  },
  noFavoriteArtistsTitle: 'You have no favorite artists',
  noFavoriteArtistsDescription: 'Click on Favorite to add an artist to your list',
  noFavoriteArtistsCardTitle: 'Artist name',
  noFavoriteArtistsCardInfo: '4 events',
  noFavoriteEventsTitle: 'You have no favorite events',
  noFavoriteEventsDescription: 'Click on Favorite to add an event to your list',
  noFavoriteExhibitionsTitle: 'You have no favorite exhibitions',
  noFavoriteExhibitionsDescription: 'Click on Favorite to add an exhibition to your list',
  noFavoriteExhibitionsCardTitle: 'Exhibition name',
  noFavoriteExhibitionsCardInfo: '4 exhibitions',
  noFavoriteEventSeriesTitle: 'You have no favorite festivals or series',
  noFavoriteEventSeriesDescription: 'Click on Favorite to add a festival or serie to your list',
  noFavoriteEventSeriesCardTitle: 'Festival or serie name',
  noFavoriteEventSeriesCardInfo: '4 festivals or series',
  noFavoriteEventsCardTitle: 'Event name',
  noFavoriteEventsCardInfo: '4 events',
  noFavoritePlacesTitle: 'You have no favorite places',
  noFavoritePlacesDescription: 'Click on Favorite to add a place to your list',
  noFavoritePlacesCardTitle: 'Place name',
  noFavoritePlacesCardInfo: '4 events',
})

export interface LvcFavoritesList {
  dataListType: FavoriteItemType
  favoriteItems: any[]
}

export class LvcFavoritesList extends MinzeElement {
  attrs: Attrs = ['data-list-type', ['data-width', 24], ['data-height', 25]]
  reactive: Reactive = [
    ['noFavorites', false],
    ['favoriteItems', []],
  ]

  updateFavoritesData(event: Event) {
    const detail = (event as CustomEvent).detail
    const favorites: FavoriteItem[] = detail.favorites
    MainModule.getInstance().loadFavoritesItems(favorites)
  }

  isEventCategory = (type: string): boolean =>
    type === FavoriteItemType.Event ||
    type === FavoriteItemType.EventOffers ||
    type === FavoriteItemType.AllEvents
  renderArtistAndPlace = (data: any, container: HTMLElement, counterEl: HTMLElement) => {
    this.favoriteItems = data
    if (this.favoriteItems?.length > 0) {
      counterEl.innerText = `(${this.favoriteItems.length})`
      container.innerHTML = `
        ${this.favoriteItems
          ?.map(item => {
            return `
          <lvc-favorite-item
            data-id="${item.id}"
            data-type="${item.schema}"
            data-url="${item.urlConstruct?.[Locale.language]}"
            data-image-url="${item.image.url}"
            data-image-alt="${item?.image.url?.[Locale.language]}"
          >
            <span slot="title">${item?.name?.[Locale.language]}</span>
            <span slot="date">${item.eventCount || 0} ${Locale.getPluralKey('favoritesList.eventsCount', item.eventCount || 0)}</span>
          </lvc-favorite-item>
        `
          })
          .join('')}
        `
    } else {
      this.noFavorites = true
    }
  }

  renderEvents = (eventsData: any, eventsContainer: HTMLElement, counterEl: HTMLElement) => {
    this.favoriteItems = eventsData
    const events = this.sortEventsByMonth(eventsData)
    if (events.length > 0) {
      if (counterEl && counterEl.parentElement) {
        counterEl.parentElement.style.display = 'none'
      }
      counterEl.innerText = `(${this.favoriteItems.length})`
      eventsContainer.innerHTML = `
        ${events
          .map((eventMonth, index) => {
            return `
          <h3 class="event-month-label">
            ${
              window.matchMedia('(min-width: 730px)').matches
                ? ''
                : `<lvc-calendar-icon></lvc-calendar-icon>`
            }
            ${(eventMonth.monthName && eventMonth.monthName[0].toUpperCase() + eventMonth.monthName.slice(1)) || ''}
          </h3>
          <lvc-primary-slider
            data-component-location="favoris"
            data-slides="${(eventMonth.data.map((event: any) => mapEvent(event)), encodeSlides(eventMonth.data.map((event: any) => mapEvent(event)) as LvcPrimarySliderItem[]))}"
            zooming-effect-disabled="true"
            fade-effect="${index > 0 ? `true` : `false`}"
            items-per-slide="${index > 0 ? `3` : `2`}"
          ></lvc-primary-slider>
        `
          })
          .join('')}
      `
    } else {
      this.noFavorites = true
    }
  }

  sortEventsByMonth = (array: any[]) => {
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()

    const monthArrays = array.reduce(
      (acc, obj) => {
        const startDate = new Date(obj.startDate * 1000)
        const endDate = obj.endDate ? new Date(obj.endDate * 1000) : startDate
        const startMonth = startDate.getMonth()
        const endMonth = endDate.getMonth()
        const startYear = startDate.getFullYear()
        const endYear = endDate.getFullYear()

        if (obj.type !== 'Permanent') {
          for (let year = startYear; year <= endYear; year++) {
            let monthStart = year === startYear ? startMonth : 0
            let monthEnd = year === endYear ? endMonth : 11

            for (let month = monthStart; month <= monthEnd; month++) {
              if (year > currentYear || (year === currentYear && month >= currentMonth)) {
                const key = `${year}-${month + 1}`
                if (!acc[key]) {
                  acc[key] = []
                }
                acc[key].push(obj)
              }
            }
          }
        }

        return acc
      },
      {} as { [key: string]: any[] }
    )

    const sortedMonths = Object.entries(monthArrays)
      .sort((a, b) => {
        const dateA = new Date(`${a[0]}-01T00:00:00+00:00`).getTime()
        const dateB = new Date(`${b[0]}-01T00:00:00+00:00`).getTime()
        return dateA - dateB
      })
      .map(([entryKey, entryValue]: [string, unknown]) => {
        const [year, month] = entryKey.split('-')

        const monthName = Locale.getKey('general.months.long.' + month)
        const data = entryValue as unknown[]
        return { monthName: `${monthName} ${year}`, data }
      })

    // We add permanent exhibitions separately
    const permanentExhibitions = array.filter(obj => obj.type === 'Permanent')
    // we check if there are permanent exhibitions and if yes, create a month object for them
    if (permanentExhibitions.length > 0) {
      const permanentMonth = {
        monthName: Locale.getKey('favoritesList.permanentExhibitions'),
        data: permanentExhibitions,
      }
      return [permanentMonth, ...sortedMonths]
    }

    return [...sortedMonths]
  }

  renderFavoriteItems(
    listHeading: HTMLElement | null,
    listEl: HTMLElement | null,
    eventsContainer: HTMLElement | null,
    favoriteExhibitionsContainer: HTMLElement | null,
    favoriteEventSeriesContainer: HTMLElement | null,
    counter: HTMLElement | null,
    listType: FavoriteItemType
  ) {
    const detail = (event as CustomEvent).detail
    const language = Locale.language

    if (
      !listEl ||
      !counter ||
      !eventsContainer ||
      !favoriteExhibitionsContainer ||
      !listHeading ||
      !favoriteEventSeriesContainer
    ) {
      console.error('One of the elements is missing')
      return
    }

    if (detail?.error) {
      this.noFavorites = true
      return
    }

    let favoriteItems = []

    if (this.isEventCategory(listType)) {
      favoriteItems = detail.data?.eventOffers?.data || []
    } else if (listType === FavoriteItemType.Exhibition) {
      favoriteItems = detail.data?.exhibitionEvents?.data || []
    } else if (listType === FavoriteItemType.EventSeries) {
      favoriteItems = detail.data?.eventSeries?.data || []
    } else {
      favoriteItems = detail.data?.[listType]?.data || []
    }

    if (favoriteItems.length > 0) {
      if (this.isEventCategory(listType)) {
        this.renderEvents(favoriteItems, eventsContainer, counter)
      } else if (listType === FavoriteItemType.Exhibition) {
        this.renderEvents(favoriteItems, favoriteExhibitionsContainer, counter)
      } else if (listType === FavoriteItemType.EventSeries) {
        this.renderEvents(favoriteItems, favoriteEventSeriesContainer, counter)
      } else {
        this.renderArtistAndPlace(favoriteItems, listEl, counter)
      }
    } else {
      this.noFavorites = true
    }
  }

  renderTitle = () => {
    switch (this.dataListType) {
      case FavoriteItemType.AllEvents:
        return Locale.getKey('favoritesList.events')
      case FavoriteItemType.Event:
        return Locale.getKey('favoritesList.events')
      case FavoriteItemType.Exhibition:
        return Locale.getKey('favoritesList.exhibitions')
      case FavoriteItemType.EventSeries:
        return Locale.getKey('favoritesList.eventSeries')
      case FavoriteItemType.EventOffers:
        return Locale.getKey('favoritesList.events')
      case FavoriteItemType.Artist:
        return Locale.getKey('favoritesList.artists')
      case FavoriteItemType.Places:
        return Locale.getKey('favoritesList.places')
      default:
        return ''
    }
  }

  getNoFavoriteText = (textName: string) => {
    switch (this.dataListType) {
      case FavoriteItemType.AllEvents:
        return Locale.getKey('favoritesList.noFavoriteEvents' + textName)
      case FavoriteItemType.Event:
        return Locale.getKey('favoritesList.noFavoriteEvents' + textName)
      case FavoriteItemType.Exhibition:
        return Locale.getKey('favoritesList.noFavoriteExhibitions' + textName)
      case FavoriteItemType.EventSeries:
        return Locale.getKey('favoritesList.noFavoriteEventSeries' + textName)
      case FavoriteItemType.EventOffers:
        return Locale.getKey('favoritesList.noFavoriteEvents' + textName)
      case FavoriteItemType.Artist:
        return Locale.getKey('favoritesList.noFavoriteArtists' + textName)
      case FavoriteItemType.Places:
        return Locale.getKey('favoritesList.noFavoritePlaces' + textName)
      default:
        return ''
    }
  }

  onFavoritesLoaded = () => {
    if (!this.shadowRoot) return
    const favoriteItemsList = this.shadowRoot.getElementById('favorite-items-list')
    const favoriteEvents = this.shadowRoot.getElementById('favorite-events')
    const favoriteExhibitions = this.shadowRoot.getElementById('favorite-exhibitions')
    const favoriteEventSeries = this.shadowRoot.getElementById('favorite-event-series')
    const favoriteListHeading = this.shadowRoot.getElementById('favorite-list-heading')
    const counter = this.shadowRoot.getElementById('counter')

    this.renderFavoriteItems(
      favoriteListHeading,
      favoriteItemsList,
      favoriteEvents,
      favoriteExhibitions,
      favoriteEventSeries,
      counter,
      this.dataListType
    )
  }

  html = () => `
    <div>
      <h3 id="favorite-list-heading">${this.renderTitle()} <span id="counter">(0)</span></h3>
      <div class="no-favorites-warning" style="display: ${this.noFavorites ? 'flex' : 'none'}">
        <div class="no-favorites-warning-content">
          <h2 class="no-favorites-warning-title">
            ${this.getNoFavoriteText('Title')}
          </h2>
          <p class="no-favorites-warning-description">
            ${this.getNoFavoriteText('Description')}
          </p>
        </div>
        <div class="no-favorites-warning-illustration-block">
          <lvc-charlie-icon
            width="${window.matchMedia('(max-width: 730px)').matches ? '159' : ''}"
            height="${window.matchMedia('(max-width: 730px)').matches ? '159' : ''}"
          ></lvc-charlie-icon>
          <div class="events_slider-slide-content-block">
            <div class="events_slider-slide-image-wrapper">
              <lvc-artist-image-placeholder-icon></lvc-artist-image-placeholder-icon>
              <lvc-favorite-icon-button></lvc-favorite-icon-button>
            </div>
            <div class="events_slider-slide-text-wrapper">
              <div class="events_slider-slide-heading">
                <h2 class="events_slider-slide-title">
                  ${this.getNoFavoriteText('CardTitle')}
                </h2>
              </div>
              <div class="events_slider-slide-info-wrapper">
                <lvc-calendar-icon
                  data-width="${window.matchMedia('(max-width: 730px)').matches ? '14' : '24'}"
                  data-height="${window.matchMedia('(max-width: 730px)').matches ? '14' : '25'}"
                ></lvc-calendar-icon>
                <div class="text-size-small">
                  ${this.getNoFavoriteText('CardInfo')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="favorite-events"></div>
      <div id="favorite-items-list"></div>
      <div id="favorite-exhibitions"></div>
      <div id="favorite-event-series"></div>
    </div>
  `

  css = () => `
    :host {
      width: 100%;
    }

    #favorite-items-list {
      display: flex;
      flex-wrap: wrap;
      gap: 40px;
    }

    #favorite-items-list > lvc-favorite-item {
      width: calc(33.33% - 26.66px);
    }

    h3 {
      font-weight: ${Font.weight.medium};
      font-size: 40px;
      line-height: 120%;
      color: #1E007D;
      margin: 40px 0;
    }

    .event-month-label{
      font-style: normal;
      font-weight: ${Font.weight.medium};
      font-size: 40px;
      line-height: 120%;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .no-favorites-warning {
      width: 100%;
      display: flex;
      justify-content: space-between;
      padding-bottom: 110px;
    }

    .no-favorites-warning h2.no-favorites-warning-title {
      font-weight: ${Font.weight.extraBold};
      font-size: 60px;
      line-height: 100%;
      color: #1E007D;
      margin: 0;
    }

    .no-favorites-warning p.no-favorites-warning-description {
      font-weight: ${Font.weight.medium};
      font-size: 20px;
      line-height: 150%;
      color: #1E007D;
    }

    .no-favorites-warning-illustration-block {
      display: flex;
      position: relative;
    }

    .no-favorites-warning-content {
      max-width: 592px;
    }

    lvc-charlie-icon {
      position: absolute;
      transform: translate(calc(-100% - 88px), calc(100% - 20px));
    }

    .events_slider-slide-content-block {
      width: 320px;
      flex: 0 auto;
      position: relative;
      color: #1e007d;
      pointer-events: none;
    }
    .events_slider-slide-image-wrapper {
      width: 100%;
      position: relative;
    }
    .events_slider-slide-text-wrapper {
      transition: all .2s;
    }
    .events_slider-slide-heading {
      margin: 1.5rem 0 4px 0;
    }
    .events_slider-slide-title {
      text-transform: uppercase;
      font-size: 1.5rem;
      margin-top: 0;
      margin-bottom: 0;
      font-weight: ${Font.weight.extraBold};
      line-height: 1.2;
    }
    .events_slider-slide-author {
      margin: 0.5rem 0 0 0;
    }
    .events_slider-slide-subtitle {
      font-size: 1.25rem;
      line-height: 1.8;
    }
    .events_slider-slide-info-wrapper {
      grid-column-gap: 8px;
      align-items: center;
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
      display: flex;
    }
    .events_slider-slide-info-icon {
      width: 24px;
      height: 24px;
      max-width: 100%;
      display: inline-block;
    }
    .events_slider-slide-button-wrapper {
      margin-top: 16px;
    }
    @media screen and (max-width: 730px) {
      .event-month-label {
        font-size: 20px;
        font-weight: ${Font.weight.medium};
        line-height: 150%;
        margin-bottom: 16px;
      }
      .no-favorites-warning {
        flex-direction: column;
      }
      h3 {
        font-size: 20px;
        margin-bottom: 16px;
      }
      .no-favorites-warning h2.no-favorites-warning-title {
        font-size: 24px;
        margin-bottom: 8px;
      }
      .no-favorites-warning p.no-favorites-warning-description {
        font-size: 14px;
        margin: 8px 0 16px 0;
      }
      lvc-charlie-icon {
        position: relative;
        right: 0;
        bottom: -163px;
        transform: none;
      }
      .events_slider-slide-content-block {
        width: 256px;
        padding-left: 35px;
      }
      .events_slider-slide-info-wrapper {
        grid-column-gap: 4px;
      }
      .no-favorites-warning-illustration-block {
        width: calc(100% + 100px);
        left: -50px;
      }
      .events_slider-slide-title {
        font-size: 14px;
      }
      .text-size-small {
        font-size: 8px;
      }
      .events_slider-slide-heading {
        margin-top: 10px;
      }
      .events_slider-slide-button-wrapper {
        margin-top: 10px;
      }
      lvc-favorite-button {
        transform: scale(0.7) translate(-20%, -20%);
      }
      #favorite-items-list > lvc-favorite-item {
        width: 100%;
      }
    }
  `

  eventListeners: EventListeners = [
    [window, 'lvc:user-data-updated', this.updateFavoritesData],
    [window, 'lvc:item-favorite-status-update', this.updateFavoritesData],
    [window, 'lvc:favorites-items-loaded', this.onFavoritesLoaded],
    [window, 'lvc:favorites-items-loading-failed', this.onFavoritesLoaded],
  ]
}
